import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { useClienteActivo } from '../../lib/cliente-activo';
import { Spinner } from '../../components/ui/Spinner';
import { Select } from '../../components/ui/Select';
import { SugerenciasCatalogo } from './SugerenciasCatalogo';
import { RappelCliente } from './RappelCliente';
import { FacturacionCliente } from './FacturacionCliente';

/**
 * Zona de análisis por cliente (docs/rediseno-ux/dashboard-propuesta.html,
 * fase 3). Responde a la pregunta del CEO: cuánto le vendemos a este cliente,
 * en unidades y en euros, por tipo de producto, y si crece o decrece respecto
 * al año anterior — pudiendo cambiar entre años, trimestres y meses.
 *
 * Convenciones que se respetan en toda la pantalla (norma IBCS + el
 * anti-patrón de periodo parcial):
 *   - el periodo en curso NUNCA se compara entero contra uno cerrado: el
 *     servidor recorta la comparación «a fecha equivalente» y aquí se dice;
 *   - el año actual va en color y el anterior en gris;
 *   - sube ≠ bueno automáticamente: el signo se colorea, el valor no.
 *
 * Datos: GET /api/analisis?cliente=…&nivel=…&anio=…&comparar=…&fuente=…
 */

type Total = { uds: number; eur: number; operaciones: number; abonos_uds: number; abonos_eur: number; meses_con_dato: number };
type FilaSerie = {
  mes: string; mes_py: string;
  eur: number | null; uds: number | null; eur_py: number; uds_py: number;
  so_uds: number | null; so_uds_py: number | null; so_provisional: boolean;
};
type FilaTax = {
  gama: string | null; categoria: string | null; subcategoria: string | null;
  uds: number; eur: number; productos: number; uds_py: number; eur_py: number;
  delta_eur: number | null; delta_uds: number | null;
};
type FilaTop = {
  producto_id: string | null; sku: string | null; nombre: string | null;
  uds: number; eur: number; uds_py: number; eur_py: number; delta_eur: number | null;
};
type Analisis = {
  cliente: { codigo: string; nombre: string };
  fuente: 'erp' | 'pedidos' | null;
  fuentes: ('erp' | 'pedidos')[];
  anios: number[];
  cobertura: {
    ventas_approx_hasta: string | null; coste_entrada_hasta: string | null; stock_hasta: string | null;
    sellout_unidad: 'mes' | 'semana' | null; sellout_hasta: string | null;
    sellin_erp_hasta: string | null; sellin_pedidos_hasta: string | null;
  };
  periodo: { nivel: 'anio' | 'trimestre' | 'mes'; anio: number; etiqueta: string; meses: string[]; parcial: boolean; meses_con_datos: string[]; meses_sin_datos: string[] };
  comparacion: { etiqueta: string; anio: number; meses_usados: string[] };
  kpis: {
    sellin: Total; sellin_comparacion: Total;
    delta_eur: number | null; delta_uds: number | null;
    asp: number | null; asp_comparacion: number | null; delta_asp: number | null;
    sellout: { uds: number; meses_con_dato: number; provisional: boolean; unidad: 'mes' | 'semana' | null };
    sellout_comparacion: { uds: number; meses_con_dato: number; provisional: boolean };
    delta_sellout: number | null;
    ratio_si_so: number | null; ratio_meses: number;
    rotura: { n: number; sin_stock: number; unidad: 'meses' | 'semanas'; umbral: number };
  } | null;
  serie: FilaSerie[];
  taxonomia: FilaTax[];
  top: FilaTop[];
  total_top: { eur: number; uds: number };
  opciones_tax: { categoria_id: string; categoria: string; subcategoria_id: string | null; subcategoria: string | null }[];
};

type Medida = 'eur' | 'uds';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const FUENTE_LABEL: Record<'erp' | 'pedidos', string> = { erp: 'Facturación APPROX', pedidos: 'Pedidos registrados' };

const valor = (m: Medida, v: number | null | undefined) => (v == null ? '—' : m === 'eur' ? fmt.eur(v) : `${fmt.int(v)} uds`);

function Delta({ v, className = '' }: { v: number | null | undefined; className?: string }) {
  if (v == null) return <span className={`whitespace-nowrap text-slate-400 ${className}`}>—</span>;
  const color = v === 0 ? 'text-slate-500' : v > 0 ? 'text-ok-600' : 'text-bad-600';
  return (
    <span className={`whitespace-nowrap font-semibold tabular-nums ${color} ${className}`}>
      {v === 0 ? '=' : v > 0 ? '▲' : '▼'} {fmt.number(Math.abs(v))} %
    </span>
  );
}

export function AnalisisCliente() {
  const { cliente, base } = useClienteActivo();
  const [sp, setSp] = useSearchParams();
  const [medida, setMedida] = useState<Medida>('eur');

  const nivel = (sp.get('nivel') ?? 'anio') as 'anio' | 'trimestre' | 'mes';
  const anio = sp.get('anio') ?? '';
  const trimestre = sp.get('trimestre') ?? '1';
  const mes = sp.get('mes') ?? '1';
  const comparar = (sp.get('comparar') ?? 'py') as 'py' | 'prev';
  const fuente = sp.get('fuente') ?? '';
  const vistaParam = sp.get('vista');
  const vista = vistaParam === 'sugerencias' || vistaParam === 'rappel' || vistaParam === 'facturacion' ? vistaParam : 'ventas';
  const categoriaId = sp.get('categoria_id') ?? '';
  const subcategoriaId = sp.get('subcategoria_id') ?? '';

  const qs = new URLSearchParams({ cliente, nivel, comparar });
  if (anio) qs.set('anio', anio);
  if (nivel === 'trimestre') qs.set('trimestre', trimestre);
  if (nivel === 'mes') qs.set('mes', mes);
  if (fuente) qs.set('fuente', fuente);
  if (categoriaId) qs.set('categoria_id', categoriaId);
  if (subcategoriaId) qs.set('subcategoria_id', subcategoriaId);

  const q = useQuery({
    queryKey: ['analisis', qs.toString()],
    queryFn: () => api.get<Analisis>(`/analisis?${qs.toString()}`),
    staleTime: 60_000,
    enabled: vista === 'ventas',
  });

  // Tipos y subtipos con productos de este cliente (para el filtro). Se
  // guardan de la última respuesta para que no parpadeen al cambiar de filtro.
  const opcionesTax = useMemo(() => q.data?.opciones_tax ?? [], [q.data]);
  const tipos = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of opcionesTax) m.set(o.categoria_id, o.categoria);
    return [...m.entries()].map(([value, label]) => ({ value, label }));
  }, [opcionesTax]);
  const subtipos = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of opcionesTax) {
      if (!o.subcategoria_id || (categoriaId && o.categoria_id !== categoriaId)) continue;
      m.set(o.subcategoria_id, categoriaId ? (o.subcategoria ?? '') : `${o.categoria} › ${o.subcategoria}`);
    }
    return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [opcionesTax, categoriaId]);
  const d = q.data;

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    setSp(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-[1400px]">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Análisis de {cliente}</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {vista === 'sugerencias'
              ? 'Qué reponer, qué acción comercial cambiar y qué productos vigilar'
              : vista === 'facturacion'
              ? 'Facturación de APPROX al cliente según el ERP, mes a mes y por categoría'
              : vista === 'rappel'
              ? 'Rappel a pagar cada trimestre según lo facturado y las condiciones comerciales'
              : <>
                {d ? (d.fuente ? `Sell-in según ${FUENTE_LABEL[d.fuente].toLowerCase()}` : 'Sin ventas registradas para este cliente') : ' '}
                {d?.cobertura.sellout_hasta ? ` · sell-out hasta ${fmt.date(d.cobertura.sellout_hasta)}` : ''}
              </>}
          </p>
        </div>
        {vista === 'ventas' && <div className="flex rounded-md bg-slate-200 p-0.5" role="group" aria-label="Medida">
          {(['eur', 'uds'] as Medida[]).map((m) => (
            <button key={m} type="button" onClick={() => setMedida(m)} aria-pressed={medida === m}
              className={`cursor-pointer rounded px-3 py-1 text-xs font-bold transition ${medida === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
              {m === 'eur' ? 'Facturación €' : 'Unidades'}
            </button>
          ))}
        </div>}
      </div>

      <div className="mb-4 flex gap-1 border-b border-slate-200" role="tablist">
        {([['ventas', 'Ventas'], ['facturacion', 'Facturación'], ['sugerencias', 'Sugerencias'], ['rappel', 'Rappel']] as const).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={vista === k} onClick={() => set('vista', k === 'ventas' ? '' : k)}
            className={`-mb-px cursor-pointer border-b-2 px-4 py-2 text-sm font-semibold transition ${vista === k ? 'border-accent-600 text-accent-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {label}
          </button>
        ))}
      </div>

      {vista === 'sugerencias' && <SugerenciasCatalogo cliente={cliente} base={base} />}

      {vista === 'rappel' && <RappelCliente cliente={cliente} />}

      {vista === 'facturacion' && <FacturacionCliente cliente={cliente} base={base} />}

      {vista === 'ventas' && <>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <Grupo label="Periodo">
          {(['anio', 'trimestre', 'mes'] as const).map((n) => (
            <Pildora key={n} activo={nivel === n} onClick={() => set('nivel', n)}>{n === 'anio' ? 'Año' : n === 'trimestre' ? 'Trimestre' : 'Mes'}</Pildora>
          ))}
        </Grupo>
        {d && d.anios.length > 0 && (
          <Grupo label="Año">
            {d.anios.map((a) => <Pildora key={a} activo={String(d.periodo.anio) === String(a)} onClick={() => set('anio', String(a))}>{a}</Pildora>)}
          </Grupo>
        )}
        {nivel === 'trimestre' && (
          <Grupo label="Trimestre">
            {[1, 2, 3, 4].map((t) => <Pildora key={t} activo={Number(trimestre) === t} onClick={() => set('trimestre', String(t))}>T{t}</Pildora>)}
          </Grupo>
        )}
        {nivel === 'mes' && (
          <Grupo label="Mes">
            {MESES_CORTOS.map((m, i) => <Pildora key={m} activo={Number(mes) === i + 1} onClick={() => set('mes', String(i + 1))}>{m}</Pildora>)}
          </Grupo>
        )}
        <Grupo label="Comparar con">
          <Pildora activo={comparar === 'py'} onClick={() => set('comparar', 'py')}>Año anterior</Pildora>
          <Pildora activo={comparar === 'prev'} onClick={() => set('comparar', 'prev')}>Periodo anterior</Pildora>
        </Grupo>
        {d && d.fuentes.length > 1 && (
          <Grupo label="Fuente">
            {d.fuentes.map((f) => <Pildora key={f} activo={d.fuente === f} onClick={() => set('fuente', f)}>{FUENTE_LABEL[f]}</Pildora>)}
          </Grupo>
        )}
        {tipos.length > 0 && (
          <Grupo label="Tipo">
            <Select aria-label="Tipo de producto" options={tipos} value={categoriaId} emptyLabel="Todos"
              onChange={(e) => { const next = new URLSearchParams(sp); if (e.target.value) next.set('categoria_id', e.target.value); else next.delete('categoria_id'); next.delete('subcategoria_id'); setSp(next, { replace: true }); }}
              className="w-40 py-1 text-xs" />
          </Grupo>
        )}
        {subtipos.length > 0 && (
          <Grupo label="Subtipo">
            <Select aria-label="Subtipo de producto" options={subtipos} value={subcategoriaId} emptyLabel="Todos"
              onChange={(e) => set('subcategoria_id', e.target.value)} className="w-52 py-1 text-xs" />
          </Grupo>
        )}
      </div>
      {(categoriaId || subcategoriaId) && (
        <p className="-mt-2 mb-3 text-xs text-accent-700">
          Analizando solo {subcategoriaId ? `el subtipo ${subtipos.find((x) => x.value === subcategoriaId)?.label ?? ''}` : `el tipo ${tipos.find((x) => x.value === categoriaId)?.label ?? ''}`}.{' '}
          <button type="button" className="cursor-pointer underline" onClick={() => { const next = new URLSearchParams(sp); next.delete('categoria_id'); next.delete('subcategoria_id'); setSp(next, { replace: true }); }}>Quitar filtro</button>
        </p>
      )}

      {q.isLoading && <div className="py-10"><Spinner /></div>}
      {q.error && <p role="alert" className="rounded-md bg-bad-50 px-3 py-2 text-sm text-bad-700">No se pudo cargar el análisis. {(q.error as Error).message}</p>}

      {d && !d.kpis && (
        <p className="rounded-lg border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">
          {cliente} no tiene ventas registradas todavía. Importa el histórico de APPROX o registra pedidos para ver el análisis.
        </p>
      )}

      {d && d.kpis && (
        <div className="space-y-4">
          <AvisoParcial d={d} />
          <Kpis d={d} medida={medida} base={base} />
          <GraficoAnual d={d} medida={medida} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Taxonomia filas={d.taxonomia} medida={medida} etiqueta={d.comparacion.etiqueta} />
            <Top filas={d.top} medida={medida} total={d.total_top[medida]} base={base} etiqueta={d.comparacion.etiqueta} />
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

function Grupo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function Pildora({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={activo}
      className={`cursor-pointer rounded-md border px-2.5 py-1 text-xs font-semibold transition ${activo ? 'border-accent-600 bg-accent-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-accent-300 hover:text-slate-900'}`}>
      {children}
    </button>
  );
}

/** El aviso que evita la lectura falsa: qué tramo se está comparando. */
function AvisoParcial({ d }: { d: Analisis }) {
  const p = d.periodo;
  const usados = d.comparacion.meses_usados.length;
  if (!p.parcial) {
    return <p className="text-xs text-slate-500">Periodo cerrado: {p.etiqueta} completo frente a {d.comparacion.etiqueta}.</p>;
  }
  const primero = p.meses_con_datos[0];
  const ultimo = p.meses_con_datos[p.meses_con_datos.length - 1];
  const etiquetaMes = (m?: string) => (m ? `${MESES[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}` : '—');
  return (
    <p className="rounded-md border border-warn-300 bg-warn-50 px-3 py-2 text-xs text-warn-700">
      <b>{p.etiqueta} va a medias.</b> Hay datos de {etiquetaMes(primero)} a {etiquetaMes(ultimo)}
      {p.meses_sin_datos.length ? ` (${p.meses_sin_datos.length === 1 ? 'falta 1 mes' : `faltan ${p.meses_sin_datos.length} meses`})` : ''}, así que la comparación usa
      solo {usados === 1 ? 'el mismo mes' : `los mismos ${usados} meses`} de {d.comparacion.etiqueta}. Comparar el
      total de un periodo a medias contra uno cerrado daría una caída inventada.
    </p>
  );
}

function Kpis({ d, medida, base }: { d: Analisis; medida: Medida; base: string }) {
  const k = d.kpis!;
  const eur = medida === 'eur';
  const principal = eur ? k.sellin.eur : k.sellin.uds;
  const principalPy = eur ? k.sellin_comparacion.eur : k.sellin_comparacion.uds;
  const delta = eur ? k.delta_eur : k.delta_uds;
  const abonos = eur ? k.sellin.abonos_eur : k.sellin.abonos_uds;
  const unidadSo = k.sellout.unidad === 'semana' ? 'semanales' : 'mensuales';
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tarjeta label={`Sell-in ${d.periodo.etiqueta}`} valor={valor(medida, principal)}
        pie={<>vs {d.comparacion.etiqueta}: {valor(medida, principalPy)} · <Delta v={delta} /></>} />
      <Tarjeta label="Precio medio de venta" valor={fmt.eur(k.asp)}
        pie={<>vs {d.comparacion.etiqueta}: {fmt.eur(k.asp_comparacion)} · <Delta v={k.delta_asp} /></>} />
      <Tarjeta label={`Sell-out (ventas ${unidadSo})`} valor={k.sellout.meses_con_dato ? `${fmt.int(k.sellout.uds)} uds` : '—'}
        pie={k.sellout.meses_con_dato
          ? <>{k.sellout_comparacion.meses_con_dato ? <>vs {d.comparacion.etiqueta}: {fmt.int(k.sellout_comparacion.uds)} · <Delta v={k.delta_sellout} /></> : 'sin periodo anterior con datos'}</>
          : 'sin sell-out importado en el periodo'} />
      <Tarjeta label="Rotación sell-in / sell-out" alerta={k.rotura.n > 0}
        valor={k.ratio_si_so != null ? `${fmt.number(k.ratio_si_so)}×` : '—'}
        pie={k.ratio_si_so != null
          ? <>sobre {k.ratio_meses} {k.ratio_meses === 1 ? 'mes' : 'meses'} con ambas fuentes · <Link className="text-accent-600 hover:underline" to={`${base}/productos?productos-${d.cliente.codigo}_f_riesgo_rotura=${k.rotura.umbral}`}>{k.rotura.n} en riesgo de rotura</Link></>
          : 'faltan meses con las dos fuentes'} />
      {abonos !== 0 && (
        <p className="col-span-2 text-xs text-slate-500 lg:col-span-4">
          Los abonos del periodo ({valor(medida, Math.abs(abonos))}) van aparte y no restan de la cifra de sell-in.
        </p>
      )}
    </div>
  );
}

function Tarjeta({ label, valor: v, pie, alerta }: { label: string; valor: ReactNode; pie?: ReactNode; alerta?: boolean }) {
  return (
    <div className={`rounded-lg border bg-white px-4 py-3 shadow-sm ${alerta ? 'border-warn-300' : 'border-slate-200'}`}>
      <div className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-500" title={label}>{label}</div>
      <div className="mt-1 font-display text-2xl font-extrabold tabular-nums text-slate-900">{v}</div>
      {pie && <div className="mt-1 truncate text-xs text-slate-500">{pie}</div>}
    </div>
  );
}

/**
 * Doce meses del año elegido (barras en color) contra el mismo mes del
 * anterior (barras grises), con el sell-out como línea. El mes en curso va
 * rayado: no está cerrado y no se puede leer como los demás.
 */
function GraficoAnual({ d, medida }: { d: Analisis; medida: Medida }) {
  const eur = medida === 'eur';
  const serie = d.serie;
  const conDatos = new Set(d.periodo.meses_con_datos);
  const delPeriodo = new Set(d.periodo.meses);
  const ultimoConDato = d.periodo.meses_con_datos[d.periodo.meses_con_datos.length - 1];

  const vals = serie.flatMap((s) => [eur ? s.eur : s.uds, eur ? s.eur_py : s.uds_py]).filter((v): v is number => v != null);
  const max = Math.max(1, ...vals);
  const soVals = serie.flatMap((s) => [s.so_uds, s.so_uds_py]).filter((v): v is number => v != null);
  const maxSo = Math.max(1, ...soVals);

  const W = 900, H = 260, padL = 8, padR = 8, padT = 12, padB = 26;
  const bw = (W - padL - padR) / 12;
  const y = (v: number, m: number) => H - padB - (v / m) * (H - padT - padB);
  const puntosSo = serie.map((s, i) => (s.so_uds == null ? null : `${(padL + bw * i + bw / 2).toFixed(1)},${y(s.so_uds, maxSo).toFixed(1)}`)).filter(Boolean).join(' ');

  const totalAC = serie.reduce((s, x) => s + ((eur ? x.eur : x.uds) ?? 0), 0);
  const totalPY = serie.reduce((s, x) => s + (eur ? x.eur_py : x.uds_py), 0);

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-3">
        <h2 className="font-display text-sm font-bold">{d.periodo.anio} mes a mes frente a {d.periodo.anio - 1}</h2>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-accent-600" /> {d.periodo.anio}: {valor(medida, totalAC)}</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-300" /> {d.periodo.anio - 1}: {valor(medida, totalPY)}</span>
          {soVals.length > 1 && <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-warn-500" /> sell-out uds</span>}
        </div>
      </header>
      <div className="px-5 py-4">
        <svg viewBox={`0 0 ${W} ${H}`} className="h-64 w-full" role="img"
          aria-label={`Ventas mensuales de ${d.periodo.anio} comparadas con ${d.periodo.anio - 1}`}>
          <defs>
            <pattern id="rayado" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="5" height="5" fill="#A9BFEA" />
              <line x1="0" y1="0" x2="0" y2="5" stroke="#2456B8" strokeWidth="2" />
            </pattern>
          </defs>
          <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#CBD5E1" strokeWidth="1" />
          {serie.map((s, i) => {
            const v = eur ? s.eur : s.uds;
            const vPy = eur ? s.eur_py : s.uds_py;
            const enCurso = s.mes === ultimoConDato && d.periodo.parcial;
            const x0 = padL + bw * i;
            // Los meses fuera del periodo elegido siguen dibujándose (dan
            // contexto) pero atenuados: el foco es el tramo seleccionado.
            const foco = delPeriodo.has(s.mes);
            return (
              <g key={s.mes} opacity={foco ? 1 : 0.35}>
                <rect x={x0 + bw * 0.14} width={bw * 0.34} y={y(vPy, max)} height={Math.max(0, H - padB - y(vPy, max))} fill="#CBD5E1">
                  <title>{`${MESES[i]} ${d.periodo.anio - 1}: ${valor(medida, vPy)}`}</title>
                </rect>
                {v != null && (
                  <rect x={x0 + bw * 0.5} width={bw * 0.34} y={y(v, max)} height={Math.max(0, H - padB - y(v, max))}
                    fill={enCurso ? 'url(#rayado)' : '#2456B8'}>
                    <title>{`${MESES[i]} ${d.periodo.anio}: ${valor(medida, v)}${enCurso ? ' (mes en curso)' : ''}`}</title>
                  </rect>
                )}
                <text x={x0 + bw / 2} y={H - 8} textAnchor="middle" fontSize="11"
                  fill={conDatos.has(s.mes) ? '#475569' : '#CBD5E1'}>{MESES_CORTOS[i]}</text>
              </g>
            );
          })}
          {soVals.length > 1 && <polyline points={puntosSo} fill="none" stroke="#D08A17" strokeWidth="2" />}
        </svg>
        <p className="mt-1 text-xs text-slate-400">
          Las barras grises son {d.periodo.anio - 1}; la rayada es el mes en curso, aún sin cerrar. El sell-out se dibuja en su propia escala, para leer la forma, no el nivel.
        </p>
      </div>
    </section>
  );
}

/** Desglose por gama › categoría › subcategoría con su variación. */
function Taxonomia({ filas, medida, etiqueta }: { filas: FilaTax[]; medida: Medida; etiqueta: string }) {
  const [abiertas, setAbiertas] = useState<Record<string, boolean>>({});
  const eur = medida === 'eur';
  const v = (f: FilaTax) => (eur ? f.eur : f.uds);
  const vPy = (f: FilaTax) => (eur ? f.eur_py : f.uds_py);

  const grupos = useMemo(() => {
    const m = new Map<string, { gama: string; total: number; totalPy: number; filas: FilaTax[] }>();
    for (const f of filas) {
      const g = f.gama ?? 'Sin gama';
      const e = m.get(g) ?? { gama: g, total: 0, totalPy: 0, filas: [] };
      e.total += v(f); e.totalPy += vPy(f); e.filas.push(f);
      m.set(g, e);
    }
    for (const g of m.values()) g.filas.sort((a, b) => v(b) - v(a));
    return [...m.values()].sort((a, b) => b.total - a.total)
      .map((g) => ({ ...g, filas: g.filas.filter((f) => v(f) !== 0), aCero: g.filas.filter((f) => v(f) === 0) }));
  }, [filas, medida]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = grupos.reduce((s, g) => s + g.total, 0) || 1;

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="font-display text-sm font-bold">Por tipo de producto</h2>
        <span className="text-xs text-slate-500">comparado con {etiqueta}</span>
      </header>
      {filas.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">Sin ventas en el periodo.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2 font-semibold">Gama / categoría</th>
                <th className="px-2 py-2 text-right font-semibold">{eur ? 'Facturación' : 'Unidades'}</th>
                <th className="px-2 py-2 text-right font-semibold">Peso</th>
                <th className="w-24 px-5 py-2 text-right font-semibold">vs {etiqueta}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grupos.map((g) => {
                const abierta = abiertas[g.gama] ?? true;
                const deltaG = g.totalPy > 0 ? ((g.total - g.totalPy) / g.totalPy) * 100 : null;
                return [
                  <tr key={g.gama} className="bg-slate-50/60">
                    <td className="px-5 py-2">
                      <button type="button" className="cursor-pointer font-bold text-slate-800 hover:underline"
                        aria-expanded={abierta} onClick={() => setAbiertas((s) => ({ ...s, [g.gama]: !abierta }))}>
                        {abierta ? '▾' : '▸'} {g.gama}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-right font-bold tabular-nums">{valor(medida, g.total)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-500">{fmt.number((g.total / total) * 100)} %</td>
                    <td className="px-5 py-2 text-right"><Delta v={deltaG} /></td>
                  </tr>,
                  ...(abierta ? g.filas.map((f, i) => (
                    <tr key={`${g.gama}-${i}`} className="hover:bg-slate-50">
                      <td className="px-5 py-1.5 pl-9 text-slate-600">
                        {f.categoria ?? 'Sin categoría'}
                        {f.subcategoria && <span className="text-slate-400"> · {f.subcategoria}</span>}
                        <span className="ml-1.5 text-[10px] text-slate-400">{f.productos} ref.</span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{valor(medida, v(f))}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{fmt.number((v(f) / total) * 100)} %</td>
                      <td className="px-5 py-1.5 text-right"><Delta v={eur ? f.delta_eur : f.delta_uds} /></td>
                    </tr>
                  )) : []),
                  ...(abierta && g.aCero.length ? [(
                    <tr key={`${g.gama}-cero`} className="text-xs text-slate-400">
                      <td className="px-5 py-1.5 pl-9" colSpan={3}>
                        {g.aCero.length === 1 ? '1 categoría sin ventas' : `${g.aCero.length} categorías sin ventas`} en el periodo
                        {' '}({g.aCero.map((f) => f.subcategoria ?? f.categoria ?? '—').join(', ')})
                      </td>
                      <td className="px-5 py-1.5 text-right"><Delta v={-100} /></td>
                    </tr>
                  )] : []),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Ranking de productos con la barra de peso acumulado (lectura Pareto). */
function Top({ filas, medida, total, base, etiqueta }: { filas: FilaTop[]; medida: Medida; total: number; base: string; etiqueta: string }) {
  const eur = medida === 'eur';
  const v = (f: FilaTop) => (eur ? f.eur : f.uds);
  const max = Math.max(1, ...filas.map(v));
  let acumulado = 0;
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 className="font-display text-sm font-bold">Productos que más venden</h2>
        <span className="text-xs text-slate-500">comparado con {etiqueta}</span>
      </header>
      {filas.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-400">Sin ventas en el periodo.</p>
      ) : (
        <ol className="divide-y divide-slate-100">
          {filas.map((f, i) => {
            acumulado += v(f);
            const pesoAcum = total > 0 ? (acumulado / total) * 100 : 0;
            const nombre = f.nombre ?? f.sku ?? '(sin nombre)';
            return (
              <li key={`${f.producto_id ?? f.sku ?? i}`} className="px-5 py-2">
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="w-4 shrink-0 text-right text-xs text-slate-400">{i + 1}</span>
                  {f.producto_id
                    ? <Link to={`${base}/productos/${f.producto_id}`} className="min-w-0 flex-1 truncate hover:underline" title={nombre}>
                        <span className="font-mono text-xs text-slate-500">{f.sku}</span> {nombre}
                      </Link>
                    : <span className="min-w-0 flex-1 truncate text-slate-600" title={nombre}><span className="font-mono text-xs text-slate-500">{f.sku}</span> {nombre}</span>}
                  <span className="shrink-0 font-semibold tabular-nums">{valor(medida, v(f))}</span>
                  <Delta v={eur ? f.delta_eur : null} className="w-20 shrink-0 text-right text-xs" />
                </div>
                <div className="mt-1 flex items-center gap-2 pl-6">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-accent-500" style={{ width: `${(v(f) / max) * 100}%` }} />
                  </div>
                  <span className="w-24 shrink-0 text-right text-[11px] text-slate-400">{fmt.number(pesoAcum)} % acumulado</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
